import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authClient = axios.create({
    baseURL: API,
    withCredentials: true,
});

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = anon, object = authenticated
    const [user, setUser] = useState(null);
    const [error, setError] = useState(null);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await authClient.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async ({ email, password }) => {
        setError(null);
        try {
            const { data } = await authClient.post("/auth/login", { email, password });
            setUser(data.user);
            return data.user;
        } catch (e) {
            const detail = formatApiError(e);
            setError(detail);
            throw new Error(detail);
        }
    };

    const register = async ({ email, password, name, role = "customer" }) => {
        setError(null);
        try {
            const { data } = await authClient.post("/auth/register", {
                email,
                password,
                name,
                role,
            });
            setUser(data.user);
            return data.user;
        } catch (e) {
            const detail = formatApiError(e);
            setError(detail);
            throw new Error(detail);
        }
    };

    const loginWithGoogle = async (credential, role = null) => {
        setError(null);
        try {
            const body = role ? { credential, role } : { credential };
            const { data } = await authClient.post("/auth/google", body);
            setUser(data.user);
            return data.user;
        } catch (e) {
            const detail = formatApiError(e);
            setError(detail);
            throw new Error(detail);
        }
    };

    const becomeMerchant = async () => {
        const { data } = await authClient.post("/auth/become-merchant");
        setUser(data.user);
        return data.user;
    };

    const logout = async () => {
        try {
            await authClient.post("/auth/logout");
        } finally {
            setUser(false);
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user && user !== false,
                isLoading: user === null,
                error,
                login,
                register,
                loginWithGoogle,
                becomeMerchant,
                logout,
                refresh: checkAuth,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}

function formatApiError(e) {
    const detail = e?.response?.data?.detail;
    if (!detail) return e?.message || "Algo salió mal. Intenta de nuevo.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail
            .map((x) => (x && typeof x.msg === "string" ? x.msg : JSON.stringify(x)))
            .join(" · ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

// Helper for admin/auth fetches outside the context
export const apiFetch = authClient;
